// Solution Splitter observability — lists solution-splitter
// runs (the solution-split pipeline that maps a student's pooled, unlabeled
// solution pages to their questions). Backed by the durable async-job store
// (7-day TTL) via the dedicated /console/solution-splits endpoint.
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useOpenOperationRow } from './OperationNav';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import { DataTable, Badge, type ColumnDef, type FilterDef } from '@shared-ui/components';
import { OperationsFilterBar } from './OperationsFilterBar';
import { operationsRowMenuItems, AGENTS_FILTER_DEF, DEFAULT_AGENTS, DEFAULT_DATE_RANGE, getSinceDate, syncOperationsFiltersToUrl, readOperationsParam, userSuggestionsFromRows } from './operationsFilters';
import type { SolutionSplitRow, SplitRunStatus } from './solutionSplitterTypes';

const pageSize = 20;

// Rows come from the durable run record, whose vocabulary is
// running | succeeded | failed. 'Queued' is deliberately absent: the record is
// written when the handler starts, so a job still in the queue has no row yet.
const STATUS_BADGE: Record<SplitRunStatus, 'success' | 'error' | 'warning' | 'default'> = {
  succeeded: 'success', failed: 'error', running: 'warning',
};

const STATUS_OPTIONS = [
  { value: 'completed', label: 'Completed' },
  { value: 'running', label: 'Running' },
  { value: 'failed', label: 'Failed' },
];

type SolutionSplitFilters = {
  dateRange: string;
  status: string;
  agents: string;
  userId: string;
};

const formatLatency = (ms?: number) => (ms != null ? `${(ms / 1000).toFixed(1)}s` : '—');
const formatCost = (usd?: number) => (usd != null ? `$${usd.toFixed(4)}` : '—');

export function SolutionSplitterList() {
  const openRow = useOpenOperationRow();
  const [searchParams, setSearchParams] = useSearchParams();
  const OP = 'solutionsplit'; // URL param namespace so operation tabs don't collide
  const [page, setPage] = useState(Number(readOperationsParam(searchParams, 'page', OP)) || 1);

  const [appliedFilters, setAppliedFilters] = useState<SolutionSplitFilters>({
    dateRange: readOperationsParam(searchParams, 'dateRange', OP) || DEFAULT_DATE_RANGE,
    status: readOperationsParam(searchParams, 'status', OP) || '',
    agents: readOperationsParam(searchParams, 'agents', OP) || DEFAULT_AGENTS,
    userId: readOperationsParam(searchParams, 'userId', OP) || '',
  });

  const extraDefs = useMemo<FilterDef[]>(() => [
    { key: 'status', kind: 'select', placeholder: 'All Statuses', options: STATUS_OPTIONS },
    AGENTS_FILTER_DEF,
  ], []);

  const handleFiltersChange = (values: Record<string, string>) => {
    const next = { ...appliedFilters, ...(values as Partial<SolutionSplitFilters>) };
    setAppliedFilters(next);
    setPage(1);
    syncOperationsFiltersToUrl(setSearchParams, next, 1, OP);
  };

  // User only: these lists carry no question filter, and offering one they
  // cannot apply would be a menu item that does nothing.
  const rowMenu = (r: SolutionSplitRow) => operationsRowMenuItems({
    userId: r.user?.id,
    userLabel: r.user?.email || r.user?.name,
    appliedUserId: appliedFilters.userId,
    apply: next => {
      const updated = { ...appliedFilters, ...next };
      setAppliedFilters(updated);
      setPage(1);
      syncOperationsFiltersToUrl(setSearchParams, updated, 1, OP);
    },
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    syncOperationsFiltersToUrl(setSearchParams, appliedFilters, newPage, OP);
  };

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['solution-splits', page, appliedFilters],
    queryFn: () => adminApi.getSolutionSplits({
      page,
      pageSize,
      status: appliedFilters.status || undefined,
      userId: appliedFilters.userId || undefined,
      agents: appliedFilters.agents || undefined,
      sinceDate: getSinceDate(appliedFilters.dateRange),
    }),
  });

  const rows = data?.splits || [];
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const userSuggestions = useMemo(() => userSuggestionsFromRows(rows), [rows]);

  const handleUserChange = (userId: string) => {
    const next = { ...appliedFilters, userId };
    setAppliedFilters(next);
    setPage(1);
    syncOperationsFiltersToUrl(setSearchParams, next, 1, OP);
  };

  const columns = useMemo<ColumnDef<SolutionSplitRow>[]>(() => [
    // startedAt is on the durable record; queuedAt only exists while the async
  // job does, so it cannot be the primary source.
  { key: 'date', header: 'Date', render: (r) => {
      const when = r.startedAt ?? r.queuedAt;
      return when ? format(new Date(when), 'MMM d, HH:mm:ss') : '—';
  } },
    {
      key: 'user', header: 'User', render: (r) => (
        <div>
          <div>{r.user?.name || '—'}</div>
          {r.user?.email && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.user.email}</div>}
        </div>
      ),
    },
    { key: 'status', header: 'Status', render: (r) => <Badge variant={STATUS_BADGE[r.status] ?? 'default'} size="sm">{r.status}</Badge> },
    {
      key: 'split', header: 'Split',
      render: (r) => (r.totalQuestions != null ? `${r.attempted ?? 0}/${r.totalQuestions}` : '—'),
    },
    { key: 'pages', header: 'Pages', render: (r) => (r.pages != null ? String(r.pages) : '—') },
    { key: 'provider', header: 'Provider', render: (r) => r.provider || '—' },
    {
      key: 'tokens', header: 'Tokens',
      render: (r) => (r.inputTokens != null ? `${r.inputTokens}→${r.outputTokens ?? 0}` : '—'),
    },
    { key: 'cost', header: 'Cost', render: (r) => formatCost(r.costUsd) },
    { key: 'latency', header: 'Latency', render: (r) => formatLatency(r.latencyMs) },
    {
      key: 'error', header: 'Error',
      render: (r) => (r.lastError?.message
        ? <span className="dc-error" title={r.lastError.message}>{r.lastError.message.slice(0, 60)}</span>
        : '—'),
    },
  ], []);

  if (error) return <div className="error">Error: {(error as Error).message}</div>;

  return (
    <>
      <OperationsFilterBar
        values={appliedFilters as unknown as Record<string, string>}
        onChange={handleFiltersChange}
        extraDefs={extraDefs}
        onUserChange={handleUserChange}
        userSuggestions={userSuggestions}
        loading={isFetching}
      />
      <DataTable
        columns={columns}
        data={rows}
        rowKey={(r) => r.id}
        onRowClick={(r) => openRow(rows, r, '/solution-splits', { page })}
        rowHref={(r) => `/solution-splits/${r.id}`}
        rowMenu={rowMenu}
        loading={isLoading}
        emptyMessage="No batch solution-extraction runs in this window."
        pagination={{ page, totalPages, total: data?.total ?? 0, onPageChange: handlePageChange }}
      />
    </>
  );
}
